import React from 'react';
import PropTypes from 'prop-types';

import Breadcrumbs from '@decisiv/ui-components/lib/components/Breadcrumbs';

import ManagementLayout from 'components/layouts/ManagementLayout';
import Toasts from 'components/Toasts';

import { PageSurface } from '../shared/styles';
import { USERS_PATH } from '../shared/paths';
import EditUserContent from './components/EditUserContent';
import { BreadcrumbBar } from './components/styles';
import { FORM_OPTIONS } from './components/UserDetailsForm';

/**
 * Edit User page (PD-4068): view and change a user's details.
 *
 * This component holds no state on purpose — see EditUserContent for why the shell
 * and the page body are split.
 *
 * The toast layer is mounted here rather than inside EditUserContent so the save
 * toasts do not re-render with every keystroke, and the breadcrumb is here for a
 * stronger version of the same reason: KeyDS `Breadcrumbs` renders `Link`, which
 * builds its styled component inside a `useMemo`. On portal's React 16 pins that
 * makes the hook count differ between mount and update, and the second render dies
 * in `areHookInputsEqual` (see PlainLink). Out here nothing re-renders it, so the
 * KeyDS component is safe to use; inside the form it would take the page down on the
 * first keystroke. PageSurface moved up with it so the strip still sits on the white
 * page rather than the shell's grey.
 */
function EditUser({
  appLinks,
  canEdit,
  currentPath,
  currentUser,
  footer,
  user,
  usersPath,
}) {
  return (
    <ManagementLayout
      appLinks={appLinks}
      currentPath={currentPath}
      currentUser={currentUser}
      footer={footer}
    >
      <PageSurface>
        <BreadcrumbBar>
          <Breadcrumbs
            aria-label="Breadcrumb"
            current="Edit User"
            path={[{ text: 'Users', to: usersPath }]}
          />
        </BreadcrumbBar>

        <EditUserContent canEdit={canEdit} user={user} usersPath={usersPath} />
      </PageSurface>

      <Toasts />
    </ManagementLayout>
  );
}

EditUser.propTypes = {
  appLinks: ManagementLayout.propTypes.appLinks,
  canEdit: PropTypes.bool,
  currentPath: PropTypes.string,
  currentUser: ManagementLayout.propTypes.currentUser,
  footer: ManagementLayout.propTypes.footer,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    options: FORM_OPTIONS.isRequired,
  }).isRequired,
  usersPath: PropTypes.string,
};

EditUser.defaultProps = {
  appLinks: [],
  canEdit: false,
  currentPath: undefined,
  currentUser: undefined,
  footer: undefined,
  usersPath: USERS_PATH,
};

export default EditUser;
